import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Megaphone, Plus, Edit2, Trash2, Eye, MousePointerClick, Percent,
  Check, X, Loader2, AlertTriangle, Power
} from 'lucide-react';
import * as api from '../lib/api';
import AdPreview from './AdPreview';

const PLACEMENT_LABELS = {
  feed: 'Community Feed',
  spaces_sidebar: 'Spaces Sidebar',
  dashboard_sidebar: 'Dashboard Sidebar',
};

const emptyForm = {
  headline: '',
  body: '',
  image_url: '',
  cta_label: 'Learn More',
  cta_url: '',
  audience: 'all',
  is_active: true,
  start_date: '',
  end_date: '',
  max_per_user_per_day: '',
  placements: [],
  every_n: 5,
};

const AdManager = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterPlacement, setFilterPlacement] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [error, setError] = useState('');

  const loadAds = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listAds();
      setAds(res.data || []);
    } catch {
      setAds([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAds(); }, [loadAds]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (ad) => {
    setEditingId(ad.id);
    setForm({
      headline: ad.headline || '',
      body: ad.body || '',
      image_url: ad.image_url || '',
      cta_label: ad.cta_label || 'Learn More',
      cta_url: ad.cta_url || '',
      audience: ad.audience || 'all',
      is_active: ad.is_active,
      start_date: ad.start_date ? ad.start_date.slice(0, 16) : '',
      end_date: ad.end_date ? ad.end_date.slice(0, 16) : '',
      max_per_user_per_day: ad.max_per_user_per_day ?? '',
      placements: (ad.placements || []).map(p => p.placement),
      every_n: (ad.placements || []).find(p => p.placement === 'feed')?.every_n || 5,
    });
    setError('');
    setShowForm(true);
  };

  const togglePlacement = (p) => {
    setForm(f => ({
      ...f,
      placements: f.placements.includes(p) ? f.placements.filter(x => x !== p) : [...f.placements, p],
    }));
  };

  const handleSave = async () => {
    if (!form.headline.trim() || !form.cta_url.trim()) {
      setError('Headline and CTA URL are required.'); return;
    }
    setSaving(true); setError('');
    const payload = {
      ...form,
      max_per_user_per_day: form.max_per_user_per_day === '' ? null : Number(form.max_per_user_per_day),
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      end_date:   form.end_date   ? new Date(form.end_date).toISOString()   : null,
      every_n: Number(form.every_n) || 5,
    };
    try {
      if (editingId) await api.updateAd(editingId, payload);
      else await api.createAd(payload);
      setShowForm(false);
      await loadAds();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save ad');
    }
    setSaving(false);
  };

  const handleToggleActive = async (ad) => {
    try {
      await api.updateAd(ad.id, { is_active: !ad.is_active });
      await loadAds();
    } catch {}
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`Delete "${ad.headline}"? This removes its impressions and clicks too.`)) return;
    try {
      await api.deleteAd(ad.id);
      await loadAds();
    } catch {}
  };

  const filtered = ads.filter(ad => {
    if (filterStatus === 'active' && !ad.is_active) return false;
    if (filterStatus === 'inactive' && ad.is_active) return false;
    if (filterPlacement !== 'all') {
      const has = (ad.placements || []).some(p => p.placement === filterPlacement);
      if (!has) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="ad-manager">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <select value={filterPlacement} onChange={e => setFilterPlacement(e.target.value)}
          className="px-3 py-2 rounded-arch border border-primary/20 bg-white/70 dark:bg-white text-sm dark:text-gray-800"
          data-testid="ad-filter-placement">
          <option value="all">All placements</option>
          <option value="feed">Community Feed</option>
          <option value="spaces_sidebar">Spaces Sidebar</option>
          <option value="dashboard_sidebar">Dashboard Sidebar</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-arch border border-primary/20 bg-white/70 dark:bg-white text-sm dark:text-gray-800"
          data-testid="ad-filter-status">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <div className="flex-1" />
        <button onClick={openCreate}
          className="px-4 py-2 rounded-arch text-sm font-medium bg-primary text-white hover:bg-primary/90 flex items-center gap-2"
          data-testid="ad-create-btn">
          <Plus className="w-4 h-4" /> New Ad
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-text-muted py-10 border border-dashed border-primary/15 rounded-arch">
          <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No ads {filterStatus !== 'all' || filterPlacement !== 'all' ? 'match these filters' : 'yet — click New Ad to create one'}.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(ad => (
            <div key={ad.id} className="border border-primary/15 rounded-arch-lg p-4 bg-white/40 dark:bg-white/5"
              data-testid={`ad-row-${ad.id}`}>
              <div className="flex gap-4 items-start">
                {ad.image_url
                  ? <img src={ad.image_url} alt="" className="w-20 h-20 rounded-arch object-cover flex-shrink-0" />
                  : <div className="w-20 h-20 rounded-arch bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Megaphone className="w-7 h-7 text-primary/50" />
                    </div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <p className="font-medium text-text-main truncate">{ad.headline}</p>
                    <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium flex-shrink-0 ${ad.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {ad.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-secondary/15 text-secondary font-medium flex-shrink-0"
                      style={{ background: 'rgba(169,156,200,0.18)', color: '#7d6dab' }}>
                      {ad.audience}
                    </span>
                  </div>
                  {ad.body && <p className="text-xs text-text-muted line-clamp-1 mt-0.5">{ad.body}</p>}
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {(ad.placements || []).map(p => (
                      <span key={p.placement} className="px-2 py-0.5 text-[10px] rounded-full bg-primary/10 text-primary">
                        {PLACEMENT_LABELS[p.placement] || p.placement}{p.placement === 'feed' && p.every_n ? ` · every ${p.every_n}` : ''}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-text-muted">
                    <span className="flex items-center gap-1" data-testid={`ad-impressions-${ad.id}`}>
                      <Eye className="w-3 h-3" /> {ad.impressions} impressions
                    </span>
                    <span className="flex items-center gap-1" data-testid={`ad-clicks-${ad.id}`}>
                      <MousePointerClick className="w-3 h-3" /> {ad.clicks} clicks
                    </span>
                    <span className="flex items-center gap-1" data-testid={`ad-ctr-${ad.id}`}>
                      <Percent className="w-3 h-3" /> {ad.ctr}% CTR
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button onClick={() => handleToggleActive(ad)} title={ad.is_active ? 'Disable' : 'Enable'}
                    className={`p-2 rounded-full ${ad.is_active ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    data-testid={`ad-toggle-${ad.id}`}>
                    <Power className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(ad)} title="Edit"
                    className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20"
                    data-testid={`ad-edit-${ad.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(ad)} title="Delete"
                    className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                    data-testid={`ad-delete-${ad.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-arch-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              data-testid="ad-form-modal">
              <div className="p-6 border-b border-border flex items-center justify-between flex-shrink-0">
                <h3 className="font-heading text-xl text-primary">{editingId ? 'Edit Ad' : 'New Ad'}</h3>
                <button onClick={() => setShowForm(false)} className="p-1 hover:bg-primary/10 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid md:grid-cols-[1fr_20rem] gap-0 flex-1 overflow-hidden">
                <div className="p-6 space-y-4 overflow-y-auto">
                {error && (
                  <div className="bg-red-100 text-red-700 px-3 py-2 rounded-arch text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {error}
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-text-muted">Headline *</label>
                  <input type="text" value={form.headline} onChange={e => setForm(f => ({...f, headline: e.target.value}))}
                    maxLength={120}
                    className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800"
                    data-testid="ad-headline-input" />
                </div>

                <div>
                  <label className="text-xs font-medium text-text-muted">Body</label>
                  <textarea value={form.body} onChange={e => setForm(f => ({...f, body: e.target.value}))}
                    rows={3} maxLength={400}
                    className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm"
                    data-testid="ad-body-input" />
                </div>

                <div>
                  <label className="text-xs font-medium text-text-muted">Image URL</label>
                  <input type="url" value={form.image_url} onChange={e => setForm(f => ({...f, image_url: e.target.value}))}
                    placeholder="https://..."
                    className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm"
                    data-testid="ad-image-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-text-muted">CTA Label</label>
                    <input type="text" value={form.cta_label} onChange={e => setForm(f => ({...f, cta_label: e.target.value}))}
                      maxLength={30}
                      className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-text-muted">CTA URL *</label>
                    <input type="url" value={form.cta_url} onChange={e => setForm(f => ({...f, cta_url: e.target.value}))}
                      placeholder="https://..."
                      className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm"
                      data-testid="ad-cta-url-input" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-text-muted">Audience</label>
                  <select value={form.audience} onChange={e => setForm(f => ({...f, audience: e.target.value}))}
                    className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white text-sm dark:text-gray-800"
                    data-testid="ad-audience-select">
                    <option value="all">All members</option>
                    <option value="brothers">Brothers only</option>
                    <option value="sisters">Sisters only</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-text-muted block mb-2">Placements</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(PLACEMENT_LABELS).map(([key, label]) => (
                      <button key={key} onClick={() => togglePlacement(key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border ${form.placements.includes(key) ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-white/10 text-text-muted border-primary/20 hover:bg-primary/5'}`}
                        data-testid={`ad-placement-${key}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {form.placements.includes('feed') && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-text-muted">
                      Insert in feed every
                      <input type="number" value={form.every_n} min={1} max={50}
                        onChange={e => setForm(f => ({...f, every_n: e.target.value}))}
                        className="w-16 px-2 py-1 rounded border border-primary/20 bg-white dark:text-gray-800" />
                      posts
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-text-muted">Start (optional)</label>
                    <input type="datetime-local" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))}
                      className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-text-muted">End (optional)</label>
                    <input type="datetime-local" value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))}
                      className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-text-muted">Max impressions / user / day (optional)</label>
                  <input type="number" min={1} value={form.max_per_user_per_day}
                    onChange={e => setForm(f => ({...f, max_per_user_per_day: e.target.value}))}
                    placeholder="Leave blank for no cap"
                    className="w-full mt-1 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm" />
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({...f, is_active: e.target.checked}))}
                    data-testid="ad-active-checkbox" />
                  Active
                </label>
                </div>

                {/* Right column: live preview */}
                <div className="border-l border-border bg-primary/5 p-5 overflow-y-auto hidden md:block">
                  <AdPreview ad={form} />
                </div>
              </div>

              <div className="p-4 border-t border-border flex justify-end gap-2 flex-shrink-0">
                <button onClick={() => setShowForm(false)}
                  className="px-4 py-2 rounded-arch text-sm hover:bg-primary/5">Cancel</button>
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 rounded-arch text-sm font-medium bg-primary text-white hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                  data-testid="ad-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingId ? 'Save Changes' : 'Create Ad'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdManager;
